import { useCallback, forwardRef, useImperativeHandle, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  removeBasicImage,
  selectEmployeeInformation,
  setBasicImage,
  updateBasicField,
} from "../../../Redux/employeeInformationSlice.js";
import styles from "./BasicInformation.module.scss";

const selectOptions = {
  bloodGroup: [
    "Select",
    "A +VE",
    "A -VE",
    "B +VE",
    "B -VE",
    "O +VE",
    "O -VE",
    "AB +VE",
    "AB -VE",
  ],
  religion: [
    "Select",
    "Christian",
    "Hindu",
    "Muslim",
    "Jain",
    "Buddhist",
    "OTHER",
  ],
  employmentType: [
    "Select",
    "Contractual",
    "Permanent",
    "Consultant",
    "Freelancer",
    "Intern",
    "Apprentice",
  ],
  employeeCategory: ["Select", "FOS", "Non-FOS", "Non FOS-tele", "Apprentice"],
};

const inputFields = [
  { name: "first_name", label: "Name", required: true },
  { name: "phoneNum", label: "Phone Number", required: true },
  {
    name: "emergency_contact_number",
    label: "Emergency Contact",
    required: true,
  },
  { name: "email_id", label: "Email" },
  {
    name: "DateofBirth",
    label: "Date of Birth",
    required: true,
    type: "date",
  },
  { name: "age", label: "Age" },
  { name: "placeofBirth", label: "Place Of Birth" },
];

const requiredFields = [
  { name: "aadhar_number", label: "Aadhaar Number" },
  { name: "first_name", label: "Name" },
  { name: "phoneNum", label: "Phone Number" },
  {
    name: "emergency_contact_number",
    label: "Emergency Contact",
  },
  { name: "DateofBirth", label: "Date of Birth" },
  { name: "blood_group", label: "Blood Group" },
  { name: "religion", label: "Religion" },
  { name: "employee_type", label: "Employment Type" },
  { name: "employee_category", label: "Employee Category" },
];

const BasicInformation = forwardRef(function BasicInformation(_, ref) {
  const dispatch = useDispatch();
  const { basicInformation: form, basicImages: images } = useSelector(
    selectEmployeeInformation,
  );

  const [errorFields, setErrorFields] = useState(new Set());

  const clearError = (name) => {
    setErrorFields((prev) => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };

  const updateField = (name, value) => {
    dispatch(updateBasicField({ name, value }));
    clearError(name);
  };

  const updateNumberField = (name, value) => {
    updateField(name, value.replace(/\D/g, ""));
  };

  const handleImageChange = (name, file) => {
    if (!file || file.size > 10 * 1024 * 1024) return;

    if (images[name]?.url) {
      URL.revokeObjectURL(images[name].url);
    }

    dispatch(
      setBasicImage({
        name,
        image: {
          name: file.name,
          url: URL.createObjectURL(file),
        },
      }),
    );

    clearError(name);
  };

  const removeImage = (name) => {
    if (images[name]?.url) {
      URL.revokeObjectURL(images[name].url);
    }

    dispatch(removeBasicImage(name));
  };

  const getMissingFields = useCallback(() => {
    const missingObjects = requiredFields.filter((field) => {
      const value = form[field.name] || "";

      if (field.name === "aadhar_number") {
        return value.length !== 12;
      }

      if (
        field.name === "phoneNum" ||
        field.name === "emergency_contact_number"
      ) {
        return value.length !== 10;
      }

      return !value || value === "Select";
    });

    if (!form.gender || form.gender === "Select") {
      missingObjects.push({ name: "gender", label: "Gender" });
    }

    const missingNames = new Set(missingObjects.map((field) => field.name));

    if (!images.aadhaarFront) missingNames.add("aadhaarFront");
    if (!images.profile) missingNames.add("profile");

    setErrorFields(missingNames);

    const missingLabels = missingObjects.map((field) => field.label);

    if (!images.aadhaarFront) missingLabels.push("Aadhaar Front image");
    if (!images.profile) missingLabels.push("Profile image");

    return missingLabels;
  }, [form, images]);

  useImperativeHandle(
    ref,
    () => ({
      getMissingFields,
      isComplete: () => getMissingFields().length === 0,
    }),
    [getMissingFields],
  );

  const renderUpload = (name, label) => {
    const image = images[name];
    const hasError = errorFields.has(name);

    console.log("basicinformation", form);

    return (
      <div className={styles.uploadBlock}>
        <label className={styles.label}>
          {label}
          <span>*</span>
        </label>

        <div className={styles.previewRow}>
          {image ? (
            <>
              <img
                className={styles.imagePreview}
                src={image.url}
                alt={`${label} preview`}
              />
              <button
                className={styles.removeButton}
                type="button"
                onClick={() => removeImage(name)}
              >
                Remove Image
              </button>
            </>
          ) : (
            <label className={styles.uploadButton}>
              Upload Image
              <input
                className={styles.hiddenInput}
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                onChange={(event) =>
                  handleImageChange(name, event.target.files?.[0])
                }
              />
            </label>
          )}
        </div>

        {hasError && (
          <p className={styles.fieldError}>Please upload this image</p>
        )}
      </div>
    );
  };

  return (
    <div className={styles.basicInformation}>
      <header className={styles.sectionHeader}>Basic Information</header>

      <div className={styles.content}>
        <p className={styles.note}>
          Note: Upload the pictures. Verify that the file is in JPG, JPEG, or
          PNG format and that its size is less than 10 MB.
        </p>

        <div className={styles.uploadGrid}>
          {renderUpload("aadhaarFront", "Aadhaar Front image")}
          {renderUpload("profile", "Profile")}
        </div>

        <div className={styles.aadhaarNumber}>
          <label className={styles.label}>
            Aadhaar Number<span>*</span>
          </label>
          <input
            value={form.aadhar_number || ""}
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={12}
            className={
              errorFields.has("aadhar_number") ? styles.inputError : ""
            }
            onChange={(event) =>
              updateNumberField("aadhar_number", event.target.value)
            }
          />
          {errorFields.has("aadhar_number") && (
            <p className={styles.fieldError}>
              Please enter a valid 12-digit Aadhaar number
            </p>
          )}
        </div>

        <div className={styles.formGrid}>
          {inputFields.slice(0, 4).map((field) => (
            <label className={styles.field} key={field.name}>
              <span>
                {field.label}
                {field.required ? <b>*</b> : null}
              </span>

              <input
                type={field.type || "text"}
                value={form[field.name] || ""}
                inputMode={
                  field.name === "phoneNum" ||
                  field.name === "emergency_contact_number"
                    ? "numeric"
                    : undefined
                }
                pattern={
                  field.name === "phoneNum" ||
                  field.name === "emergency_contact_number"
                    ? "[0-9]*"
                    : undefined
                }
                maxLength={
                  field.name === "phoneNum" ||
                  field.name === "emergency_contact_number"
                    ? 10
                    : undefined
                }
                className={errorFields.has(field.name) ? styles.inputError : ""}
                onChange={(event) => {
                  if (
                    field.name === "phoneNum" ||
                    field.name === "emergency_contact_number"
                  ) {
                    updateNumberField(field.name, event.target.value);
                  } else {
                    updateField(field.name, event.target.value);
                  }
                }}
              />

              {errorFields.has(field.name) && (
                <p className={styles.fieldError}>
                  {field.name === "phoneNum" ||
                  field.name === "emergency_contact_number"
                    ? "Please enter a valid 10-digit number"
                    : "Please fill this field"}
                </p>
              )}
            </label>
          ))}

          <fieldset className={styles.genderField}>
            <legend>
              Gender<b>*</b>
            </legend>

            {["male", "female", "other"].map((gender) => (
              <label key={gender}>
                <input
                  type="radio"
                  name="gender"
                  checked={form.gender === gender}
                  onChange={() => updateField("gender", gender)}
                />
                {gender === "other"
                  ? "Other"
                  : gender.charAt(0).toUpperCase() + gender.slice(1)}
              </label>
            ))}
          </fieldset>

          {inputFields.slice(4).map((field) => (
            <label className={styles.field} key={field.name}>
              <span>
                {field.label}
                {field.required ? <b>*</b> : null}
              </span>

              <input
                type={field.type || "text"}
                value={form[field.name] || ""}
                className={errorFields.has(field.name) ? styles.inputError : ""}
                onChange={(event) =>
                  updateField(field.name, event.target.value)
                }
              />

              {errorFields.has(field.name) && (
                <p className={styles.fieldError}>Please fill this field</p>
              )}
            </label>
          ))}

          <label className={styles.field}>
            <span>
              Blood Group<b>*</b>
            </span>
            <select
              value={form.blood_group || "Select"}
              className={
                errorFields.has("blood_group") ? styles.inputError : ""
              }
              onChange={(event) =>
                updateField("blood_group", event.target.value)
              }
            >
              {selectOptions.bloodGroup.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errorFields.has("blood_group") && (
              <p className={styles.fieldError}>Please select a blood group</p>
            )}
          </label>

          <label className={styles.field}>
            <span>
              Religion<b>*</b>
            </span>
            <select
              value={form.religion || "Select"}
              className={errorFields.has("religion") ? styles.inputError : ""}
              onChange={(event) => updateField("religion", event.target.value)}
            >
              {selectOptions.religion.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errorFields.has("religion") && (
              <p className={styles.fieldError}>Please select a religion</p>
            )}
          </label>

          <label className={styles.field}>
            <span>Personal Identification Mark</span>
            <input
              value={form.personal_identification_mark || ""}
              onChange={(event) =>
                updateField("personal_identification_mark", event.target.value)
              }
            />
          </label>

          <label className={styles.field}>
            <span>
              Employment Type<b>*</b>
            </span>
            <select
              value={form.employee_type || "Select"}
              className={
                errorFields.has("employee_type") ? styles.inputError : ""
              }
              onChange={(event) =>
                updateField("employee_type", event.target.value)
              }
            >
              {selectOptions.employmentType.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errorFields.has("employee_type") && (
              <p className={styles.fieldError}>
                Please select an employment type
              </p>
            )}
          </label>

          <label className={styles.field}>
            <span>
              Employee Category<b>*</b>
            </span>
            <select
              value={form.employee_category || "Select"}
              className={
                errorFields.has("employee_category") ? styles.inputError : ""
              }
              onChange={(event) =>
                updateField("employee_category", event.target.value)
              }
            >
              {selectOptions.employeeCategory.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errorFields.has("employee_category") && (
              <p className={styles.fieldError}>
                Please select an employee category
              </p>
            )}
          </label>

          <label className={styles.field}>
            <span>Referral Number</span>
            <input
              value={form.referral_number || ""}
              onChange={(event) =>
                updateField("referral_number", event.target.value)
              }
            />
          </label>
        </div>
      </div>
    </div>
  );
});

export default BasicInformation;
